import { Fragment, type ReactNode } from "react"
import { Spoiler } from "@/shared/ui/spoiler"

type Wrap = (children: ReactNode, key: number) => ReactNode

/** Теги parse_mode=HTML, которые Telegram показывает; всё прочее выводится как текст внутри. */
const TAGS: Record<string, Wrap> = {
	b: (children, key) => <b key={key}>{children}</b>,
	strong: (children, key) => <b key={key}>{children}</b>,
	i: (children, key) => <i key={key}>{children}</i>,
	em: (children, key) => <i key={key}>{children}</i>,
	u: (children, key) => <u key={key}>{children}</u>,
	ins: (children, key) => <u key={key}>{children}</u>,
	s: (children, key) => <s key={key}>{children}</s>,
	strike: (children, key) => <s key={key}>{children}</s>,
	del: (children, key) => <s key={key}>{children}</s>,
	code: (children, key) => (
		<code key={key} className="rounded bg-black/30 px-1 font-mono text-[0.9em]">
			{children}
		</code>
	),
	pre: (children, key) => (
		<pre key={key} className="my-1 overflow-x-auto rounded bg-black/30 p-2 font-mono text-xs">
			{children}
		</pre>
	),
	blockquote: (children, key) => (
		<blockquote key={key} className="my-1 border-l-2 border-current/40 pl-2">
			{children}
		</blockquote>
	),
	"tg-spoiler": (children, key) => <Spoiler key={key}>{children}</Spoiler>,
}

const SAFE_LINK = /^(https?:|tg:)/i

function renderNode(node: Node, key: number): ReactNode {
	if (node.nodeType === Node.TEXT_NODE) return node.textContent
	if (node.nodeType !== Node.ELEMENT_NODE) return null
	const element = node as Element
	const children = Array.from(element.childNodes).map(renderNode)
	const tag = element.tagName.toLowerCase()

	if (tag === "a") {
		const href = element.getAttribute("href") ?? ""
		if (!SAFE_LINK.test(href)) return <Fragment key={key}>{children}</Fragment>
		return (
			<a key={key} href={href} target="_blank" rel="noreferrer noopener" className="underline">
				{children}
			</a>
		)
	}
	if (tag === "span" && element.classList.contains("tg-spoiler"))
		return TAGS["tg-spoiler"](children, key)
	const wrap = TAGS[tag]
	return wrap ? wrap(children, key) : <Fragment key={key}>{children}</Fragment>
}

/**
 * Ответ бота в тест-чате так, как его покажет Telegram. HTML разбирается
 * DOMParser'ом и собирается в React-элементы: innerHTML не используется,
 * поэтому скрипты и обработчики из текста сценария не выполнятся.
 */
export function TelegramHtml({ html }: { html: string }) {
	const doc = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html")
	return <>{Array.from(doc.body.childNodes).map(renderNode)}</>
}
